import React from "react";
import { Card, CardContent, CardHeader } from "../../../../components/ui/card";
import SelectController from "../../../form-fields/SelectController";
import AmountController from "../../../form-fields/AmountController";
import PercentageController from "../../../form-fields/PercentageController";
import SwitchController from "../../../form-fields/SwitchController";
import IntegerController from "../../../form-fields/IntegerController";

const SectionFields403b = ({ client }: { client: ClientType }) => {
  return (
    <div>
      {/* Basic Information */}
      <Card className="mb-8 shadow">
        <CardHeader>
          <h4 className="text-lg font-semibold text-gray-700">
            Basic Information
          </h4>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <IntegerController
            name="t_403b_currentAge"
            label="Current Age"
            placeholder="Enter age"
            infoMessage="Specify the user's current age."
            defaultValue={client?.t_403b_currentAge ?? 0}
          />
          <IntegerController
            name="t_403b_plannedRetirementAge"
            label="Planned Retirement Age"
            placeholder="Enter age"
            infoMessage="Desired retirement age."
            defaultValue={client?.t_403b_plannedRetirementAge ?? 65}
          />
          <IntegerController
            name="t_403b_lifeExpectancy"
            label="Life Expectancy"
            placeholder="Enter life expectancy"
            infoMessage="Estimated life expectancy."
            defaultValue={client?.t_403b_lifeExpectancy ?? 85}
          />
        </CardContent>
      </Card>

      {/* Contribution and Employer Match */}
      <Card className="mb-8 shadow">
        <CardHeader>
          <h4 className="text-lg font-semibold text-gray-700">
            Contribution and Employer Match
          </h4>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <AmountController
            name="t_403b_currentBalance"
            label="Current 403(b) Balance ($)"
            placeholder="Enter balance"
            infoMessage="Current amount in the 403(b) account."
            defaultValue={client?.t_403b_currentBalance ?? 0.0}
          />
          <AmountController
            name="t_403b_annualContribution"
            label="Annual Contribution ($)"
            placeholder="Enter annual contribution"
            infoMessage="Yearly contribution to the 403(b) account."
            defaultValue={client?.t_403b_annualContribution ?? 0.0}
          />
          <PercentageController
            name="t_403b_employerMatchPercentage"
            label="Employer Match Percentage"
            placeholder="Enter percentage"
            infoMessage="Percentage of employer match."
            defaultValue={client?.t_403b_employerMatchPercentage ?? 0.0}
          />
          <AmountController
            name="t_403b_employerMatchCap"
            label="Employer Match Cap ($)"
            placeholder="Enter cap"
            infoMessage="Maximum annual employer contribution."
            defaultValue={client?.t_403b_employerMatchCap ?? 0.0}
          />
        </CardContent>
      </Card>

      {/* Investment Growth and Inflation */}
      <Card className="mb-8 shadow">
        <CardHeader>
          <h4 className="text-lg font-semibold text-gray-700">
            Investment Growth and Inflation
          </h4>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <PercentageController
            name="t_403b_expectedAnnualReturnRate"
            label="Expected Annual Return Rate (%)"
            placeholder="Enter rate"
            infoMessage="Assumed annual growth rate of the account."
            defaultValue={client?.t_403b_expectedAnnualReturnRate ?? 0.0}
          />
          <PercentageController
            name="t_403b_inflationRate"
            label="Inflation Rate (%)"
            placeholder="Enter rate"
            infoMessage="Estimated annual inflation rate."
            defaultValue={client?.t_403b_inflationRate ?? 0.0}
          />
          <SelectController
            name="t_403b_contributionFrequency"
            label="Contribution Frequency"
            options={[
              { value: "monthly", label: "Monthly" },
              { value: "bi-weekly", label: "Bi-Weekly" },
            ]}
            defaultValue={client?.t_403b_contributionFrequency ?? "monthly"}
            infoMessage="Frequency of contributions."
          />
        </CardContent>
      </Card>

      {/* Retirement Income and Withdrawals */}
      <Card className="mb-8 shadow">
        <CardHeader>
          <h4 className="text-lg font-semibold text-gray-700">
            Retirement Income and Withdrawals
          </h4>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <AmountController
            name="t_403b_annualSalary"
            label="Annual Salary ($)"
            placeholder="Enter salary"
            infoMessage="Annual salary for percentage-based contributions."
            defaultValue={client?.t_403b_annualSalary ?? 0.0}
          />
          <PercentageController
            name="t_403b_contributionPercentage"
            label="Contribution Percentage (%)"
            placeholder="Enter percentage"
            infoMessage="Percentage of salary contributed to 403(b)."
            defaultValue={client?.t_403b_contributionPercentage ?? 0.0}
          />
          <AmountController
            name="t_403b_desiredAnnualIncomeInRetirement"
            label="Desired Annual Income in Retirement ($)"
            placeholder="Enter income"
            infoMessage="Amount to withdraw annually in retirement."
            defaultValue={client?.t_403b_desiredAnnualIncomeInRetirement ?? 0.0}
          />
          <PercentageController
            name="t_403b_withdrawalRate"
            label="Withdrawal Rate (%)"
            placeholder="Enter rate"
            infoMessage="Annual withdrawal percentage of the account."
            defaultValue={client?.t_403b_withdrawalRate ?? 4.0}
          />
          <IntegerController
            name="t_403b_payoutPeriod"
            label="Payout Period (Years)"
            placeholder="Enter years"
            infoMessage="Number of years over which funds will be withdrawn."
            defaultValue={client?.t_403b_payoutPeriod ?? 30}
          />
          <IntegerController
            name="t_403b_startWithdrawalAge"
            label="Start Withdrawal Age"
            placeholder="Enter age"
            infoMessage="Age to begin withdrawals."
            defaultValue={client?.t_403b_startWithdrawalAge ?? 65}
          />
        </CardContent>
      </Card>

      {/* Tax and Additional Income Sources */}
      <Card className="mb-8 shadow">
        <CardHeader>
          <h4 className="text-lg font-semibold text-gray-700">
            Tax and Additional Income Sources
          </h4>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <PercentageController
            name="t_403b_marginalTaxRateInRetirement"
            label="Marginal Tax Rate in Retirement (%)"
            placeholder="Enter tax rate"
            infoMessage="Estimated tax rate during retirement."
            defaultValue={client?.t_403b_marginalTaxRateInRetirement ?? 0.0}
          />
          <PercentageController
            name="t_403b_stateTaxRate"
            label="State Tax Rate (%)"
            placeholder="Enter rate"
            infoMessage="State tax rate during retirement."
            defaultValue={client?.t_403b_stateTaxRate ?? 0.0}
          />
          <PercentageController
            name="t_403b_postRetirementReturnRate"
            label="Post-Retirement Return Rate (%)"
            placeholder="Enter rate"
            infoMessage="Growth rate after retirement."
            defaultValue={client?.t_403b_postRetirementReturnRate ?? 0.0}
          />
          <AmountController
            name="t_403b_socialSecurityIncome"
            label="Social Security Income ($)"
            placeholder="Enter income"
            infoMessage="Expected social security benefits in retirement."
            defaultValue={client?.t_403b_socialSecurityIncome ?? 0.0}
          />
          <AmountController
            name="t_403b_otherRetirementIncomeSources"
            label="Other Retirement Income Sources ($)"
            placeholder="Enter amount"
            infoMessage="Other sources of retirement income."
            defaultValue={client?.t_403b_otherRetirementIncomeSources ?? 0.0}
          />
        </CardContent>
      </Card>

      {/* Early Withdrawals */}
      <Card className="mb-8 shadow">
        <CardHeader>
          <h4 className="text-lg font-semibold text-gray-700">
            Early Withdrawals
          </h4>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <PercentageController
            name="t_403b_earlyWithdrawalPenaltyPercentage"
            label="Early Withdrawal Penalty (%)"
            placeholder="Enter penalty percentage"
            infoMessage="Penalty for early withdrawals."
            defaultValue={
              client?.t_403b_earlyWithdrawalPenaltyPercentage ?? 0.0
            }
          />
          <IntegerController
            name="t_403b_ageAtEarlyWithdrawal"
            label="Age at Early Withdrawal"
            placeholder="Enter age"
            infoMessage="Age when early withdrawal is considered."
            defaultValue={client?.t_403b_ageAtEarlyWithdrawal ?? 0}
          />
          <SwitchController
            name="t_403b_exemptionsFromEarlyWithdrawalPenalty"
            label="Exempt from Early Withdrawal Penalty"
            defaultValue={
              client?.t_403b_exemptionsFromEarlyWithdrawalPenalty ?? false
            }
            infoMessage="Indicate if eligible for penalty exemptions."
          />
        </CardContent>
      </Card>

      {/* Contribution Limits and RMDs */}
      <Card className="mb-8 shadow">
        <CardHeader>
          <h4 className="text-lg font-semibold text-gray-700">
            Contribution Limits and RMDs
          </h4>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <AmountController
            name="t_403b_irsContributionLimit"
            label="IRS Contribution Limit ($)"
            placeholder="Enter limit"
            infoMessage="Maximum allowable annual contribution to the 403(b)."
            defaultValue={client?.t_403b_irsContributionLimit ?? 0.0}
          />
          <AmountController
            name="t_403b_catchUpContributions"
            label="Catch-Up Contributions ($)"
            placeholder="Enter amount"
            infoMessage="Additional contributions allowed if over 50."
            defaultValue={client?.t_403b_catchUpContributions ?? 0.0}
          />
          <IntegerController
            name="t_403b_rmdStartAge"
            label="RMD Start Age"
            placeholder="Enter age"
            infoMessage="Age to start Required Minimum Distributions."
            defaultValue={client?.t_403b_rmdStartAge ?? 72}
          />
          <SwitchController
            name="t_403b_beneficiaryInfoForRMDs"
            label="RMD Beneficiary Information"
            defaultValue={client?.t_403b_beneficiaryInfoForRMDs ?? false}
            infoMessage="Specify if RMDs adjust for a beneficiary."
          />
        </CardContent>
      </Card>

      {/* Loans and Fees */}
      <Card className="mb-8 shadow">
        <CardHeader>
          <h4 className="text-lg font-semibold text-gray-700">
            Loans and Fees
          </h4>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <AmountController
            name="t_403b_loanBalance"
            label="Loan Balance ($)"
            placeholder="Enter balance"
            infoMessage="Outstanding loan balance from the 403(b)."
            defaultValue={client?.t_403b_loanBalance ?? 0.0}
          />
          <IntegerController
            name="t_403b_loanRepaymentTerm"
            label="Loan Repayment Term (Years)"
            placeholder="Enter term"
            infoMessage="Repayment term for the loan."
            defaultValue={client?.t_403b_loanRepaymentTerm ?? 0}
          />
          <PercentageController
            name="t_403b_loanInterestRate"
            label="Loan Interest Rate (%)"
            placeholder="Enter rate"
            infoMessage="Interest rate for the loan."
            defaultValue={client?.t_403b_loanInterestRate ?? 0.0}
          />
          <SelectController
            name="t_403b_investmentMix"
            label="Investment Mix"
            options={[
              { value: "stocks", label: "Stocks" },
              { value: "bonds", label: "Bonds" },
              { value: "mixed", label: "Mixed" },
            ]}
            defaultValue={client?.t_403b_investmentMix ?? "mixed"}
            infoMessage="Asset allocation within the 403(b)."
          />
          <PercentageController
            name="t_403b_expenseRatio"
            label="Expense Ratio (%)"
            placeholder="Enter ratio"
            infoMessage="Annual fees as a percentage of assets."
            defaultValue={client?.t_403b_expenseRatio ?? 0.0}
          />
          <AmountController
            name="t_403b_administrativeFees"
            label="Administrative Fees ($)"
            placeholder="Enter fees"
            infoMessage="Additional administrative fees for the account."
            defaultValue={client?.t_403b_administrativeFees ?? 0.0}
          />
        </CardContent>
      </Card>

      {/* Employer Contribution Vesting */}
      <Card className="mb-8 shadow">
        <CardHeader>
          <h4 className="text-lg font-semibold text-gray-700">
            Employer Contribution Vesting
          </h4>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <PercentageController
            name="t_403b_employerContributionVestingPercentage"
            label="Employer Contribution Vesting (%)"
            placeholder="Enter percentage"
            infoMessage="Vested percentage of employer contributions."
            defaultValue={
              client?.t_403b_employerContributionVestingPercentage ?? 100.0
            }
          />
          <IntegerController
            name="t_403b_yearsToFullVesting"
            label="Years to Full Vesting"
            placeholder="Enter years"
            infoMessage="Time for employer contributions to fully vest."
            defaultValue={client?.t_403b_yearsToFullVesting ?? 0}
          />
        </CardContent>
      </Card>

      {/* Cost-of-Living Adjustment (COLA) */}
      <Card className="mb-8 shadow">
        <CardHeader>
          <h4 className="text-lg font-semibold text-gray-700">
            Cost-of-Living Adjustment (COLA)
          </h4>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <PercentageController
            name="t_403b_colaPercentage"
            label="COLA Percentage (%)"
            placeholder="Enter percentage"
            infoMessage="Anticipated cost-of-living adjustments for retirement."
            defaultValue={client?.t_403b_colaPercentage ?? 0.0}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default SectionFields403b;
